import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db, updateDocument } from '../services/firebase'
import { collection, query, orderBy, limit, getDocs, addDoc } from 'firebase/firestore'
import { resolveNotificationMessage, matchTitleI18nParams } from '../utils/notificationMessage'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import logo from '../assets/logo.png'
import SearchBar from '../components/common/SearchBar'
import CenterPitchSlots from '../components/common/CenterPitchSlots'
import { getCurrentPosition, calculateDistance, sortMatchesByDistanceAndTime, getDerivedMatchStatus, getMatchRosterCapacity } from '../utils/helpers'
import {
  TrophyIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  FireIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline'

const Home = () => {
  const { t } = useTranslation()
  const { user, userData } = useAuth()
  const [banners, setBanners] = useState([])
  const [currentBanner, setCurrentBanner] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [matches, setMatches] = useState([])
  const [users, setUsers] = useState([])
  const [centers, setCenters] = useState([])
  const [currentHeroMatch, setCurrentHeroMatch] = useState(0)
  const [loading, setLoading] = useState(true)
  const [nearMeActive, setNearMeActive] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [stats, setStats] = useState({
    totalMatches: 0,
    activePlayers: 0,
    sportsCenters: 0
  })

  useEffect(() => {
    fetchBanners()
    fetchMatches()
    fetchSearchData()
    fetchStats()
  }, [user])

  useEffect(() => {
    fetchMatches()
  }, [nearMeActive, userLocation])

  useEffect(() => {
    if (banners.length <= 1) return

    const slideTimer = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % banners.length)
    }, 9000)

    return () => clearInterval(slideTimer)
  }, [banners])

  useEffect(() => {
    const heroMatchCount = Math.min(matches.length, 5)
    if (heroMatchCount <= 1) return

    const matchSlideTimer = setInterval(() => {
      setCurrentHeroMatch(prev => (prev + 1) % heroMatchCount)
    }, 3200)

    return () => clearInterval(matchSlideTimer)
  }, [matches])

  useEffect(() => {
    setCurrentHeroMatch(0)
  }, [matches])

  const fetchBanners = async () => {
    try {
      const bannersSnapshot = await getDocs(collection(db, 'banners'))
      const bannersData = bannersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(banner => banner.active !== false && (banner.position === 'home' || !banner.position))
        .sort((a, b) => (a.order || 0) - (b.order || 0))

      setBanners(bannersData)
      setCurrentBanner(0)
    } catch (error) {
      console.error('Error fetching banners:', error)
    }
  }

  const fetchMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches')
      const q = query(matchesRef, orderBy('date', 'asc'), limit(50))
      const querySnapshot = await getDocs(q)
      const allMatches = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      const now = new Date()
      const userCity = userData?.city?.trim().toLowerCase() || ''
      
      let upcomingMatches = allMatches
        .filter((m) => {
          const derivedStatus = getDerivedMatchStatus(m.date, m.status)
          return derivedStatus === 'upcoming' || derivedStatus === 'ongoing'
        })
        .sort((a, b) => {
          // Same city first, then by distance
          if (userCity) {
            const aCity = String(a.city || '').trim().toLowerCase()
            const bCity = String(b.city || '').trim().toLowerCase()
            const aSameCity = aCity === userCity
            const bSameCity = bCity === userCity
            if (aSameCity && !bSameCity) return -1
            if (!aSameCity && bSameCity) return 1
          }
          // Then sort by distance if user has location
          const userLat = Number(userData?.latitude)
          const userLon = Number(userData?.longitude)
          if (Number.isFinite(userLat) && Number.isFinite(userLon)) {
            const aLat = Number(a.latitude), aLon = Number(a.longitude)
            const bLat = Number(b.latitude), bLon = Number(b.longitude)
            const aHasCoords = Number.isFinite(aLat) && Number.isFinite(aLon)
            const bHasCoords = Number.isFinite(bLat) && Number.isFinite(bLon)
            if (aHasCoords && !bHasCoords) return -1
            if (!aHasCoords && bHasCoords) return 1
            if (aHasCoords && bHasCoords) {
              const distA = calculateDistance(userLat, userLon, aLat, aLon)
              const distB = calculateDistance(userLat, userLon, bLat, bLon)
              return distA - distB
            }
          }
          return 0
        })

      if (nearMeActive && userLocation) {
        const matchesWithCoords = upcomingMatches.filter(m => m.latitude && m.longitude)
        
        if (matchesWithCoords.length > 0) {
          const sortedMatches = sortMatchesByDistanceAndTime(matchesWithCoords, userLocation, now)
          setMatches(sortedMatches.slice(0, 3))
        } else {
          toast.error(t('matches.noMatchesNearby'))
          setNearMeActive(false)
          setUserLocation(null)
          setMatches(upcomingMatches.slice(0, 3))
        }
      } else {
        setMatches(upcomingMatches.slice(0, 3))
      }
    } catch (error) {
      console.error('Error fetching matches:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSearchData = async () => {
    try {
      const [usersSnapshot, centersSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'sportsCenters'))
      ])

      setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
      setCenters(centersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error('Error fetching home search data:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const matchesSnapshot = await getDocs(collection(db, 'matches'))
      const usersSnapshot = await getDocs(collection(db, 'users'))
      const centersSnapshot = await getDocs(collection(db, 'sportsCenters'))

      setStats({
        totalMatches: matchesSnapshot.size,
        activePlayers: usersSnapshot.size,
        sportsCenters: centersSnapshot.size
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleNearMe = async () => {
    if (nearMeActive) {
      setNearMeActive(false)
      setUserLocation(null)
      return
    }

    setLocationLoading(true)
    console.log('Home: Starting geolocation request...')
    try {
      const position = await getCurrentPosition()
      console.log('Home: Got position:', position)
      setUserLocation(position)
      setNearMeActive(true)
    } catch (error) {
      console.error('Home: Error getting location:', error.code, error.message)
      toast.error(t('location.errorGettingLocation'))
    } finally {
      setLocationLoading(false)
    }
  }

  const getMatchDistance = (match) => {
    if (!userLocation || !match.latitude || !match.longitude) return null
    return calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      match.latitude,
      match.longitude
    )
  }

  const getAcceptedCount = (match) => (
    match?.participants?.filter((participant) => participant.status === 'accepted').length
      ?? (match?.currentPlayers || 0)
  )

  const features = [
    {
      icon: <TrophyIcon className="h-8 w-8" />,
      title: t('home.organizeMatches'),
      description: t('home.organizeMatchesDesc')
    },
    {
      icon: <UserGroupIcon className="h-8 w-8" />,
      title: t('home.findPlayers'),
      description: t('home.findPlayersDesc')
    },
    {
      icon: <CalendarIcon className="h-8 w-8" />,
      title: t('home.bookCourts'),
      description: t('home.bookCourtsDesc')
    },
    {
      icon: <ArrowTrendingUpIcon className="h-8 w-8" />,
      title: t('home.trackStats'),
      description: t('home.trackStatsDesc')
    }
  ]

  const heroMatches = matches.slice(0, 5)
  const activeHeroMatch = heroMatches[currentHeroMatch]

  const formatHeroMatchDate = (date) => {
    if (!date) return 'Date TBD'
    const parsedDate = new Date(date)
    if (Number.isNaN(parsedDate.getTime())) return 'Date TBD'
    return parsedDate.toLocaleString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleJoinMatch = async (match) => {
    if (!user || !userData) {
      toast.error(t('matchDetails.mustBeLoggedInToJoin'))
      return
    }

    const alreadyJoined = match.participants?.some(p => p.userId === user.uid)
    if (alreadyJoined) {
      toast.error(t('matchDetails.alreadyInMatch'))
      return
    }

    const cap = getMatchRosterCapacity(match)
    const isRosterFull = cap > 0 && getAcceptedCount(match) >= cap
    if (isRosterFull) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    try {
      const isOrganizerJoining = match.organizerId === user.uid
      const updatedParticipants = [
        ...(match.participants || []),
        {
          userId: user.uid,
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          role: userData.role || '',
          status: isOrganizerJoining ? 'accepted' : 'pending',
          joinedAt: new Date().toISOString()
        }
      ]

      const updateData = {
        participants: updatedParticipants,
        updatedAt: new Date().toISOString()
      }

      if (isOrganizerJoining) {
        updateData.currentPlayers = (match.currentPlayers || 0) + 1
      }

      await updateDocument('matches', match.id, updateData)
      // Notify organizer when user requests to join
      if (!isOrganizerJoining && match.organizerId) {
        try {
          const senderName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || t('common.player')
          const titleParams = matchTitleI18nParams(match)
          const previewSnippet = ''
          await addDoc(collection(db, 'notifications'), {
            userId: match.organizerId,
            type: 'join_request',
            messageI18nKey: 'matchDetails.joinRequestNotification',
            messageI18nParams: { ...titleParams, senderName },
            message: resolveNotificationMessage(t, {
              messageI18nKey: 'matchDetails.joinRequestNotification',
              messageI18nParams: { ...titleParams, senderName },
              message: previewSnippet
            }),
            matchId: match.id,
            senderId: user.uid,
            senderName,
            read: false,
            createdAt: new Date().toISOString()
          })
        } catch (notifyErr) {
          console.error('Error creating join_request notification:', notifyErr)
        }
      }
      toast.success(isOrganizerJoining ? t('matchDetails.joinedMatch') : t('matchDetails.requestSent'))
    } catch (error) {
      console.error('Error joining match:', error)
      toast.error(`Error: ${error.code || error.message || 'Unknown error'}`)
    }
  }

  const getSportIcon = (sport) => {
    return sport === 'calcio' ? '⚽' : '🎾'
  }

  const getSportDisplay = (match) => {
    const emoji = getSportIcon(match?.sport)
    const typeRaw = match?.matchType || ''
    const typeWithVs = typeRaw.replace(/v/gi, 'vs')
    const sportLabel = match?.sport === 'calcio' ? t('sports.calcio') : t('sports.padel')
    return `${emoji} ${typeWithVs} ${sportLabel}`.trim()
  }

  const getMatchTime = (match) => {
    if (!match?.date) return t('common.dateTbd')
    const date = new Date(match.date)
    if (Number.isNaN(date.getTime())) return t('common.dateTbd')
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  }

  const getMatchDate = (match) => {
    if (!match?.date) return t('common.dateTbd')
    const date = new Date(match.date)
    if (Number.isNaN(date.getTime())) return t('common.dateTbd')
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getWhere = (match) => {
    return match?.location || match?.address || t('common.locationTbd')
  }

  const getCity = (match) => {
    if (match?.city) return match.city
    const source = match?.address || match?.location || ''
    if (!source.includes(',')) return t('common.locationTbd')

    const parts = source
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)

    if (parts.length === 0) return t('common.locationTbd')

    const last = parts[parts.length - 1]
    if (/\d/.test(last) && parts.length > 1) {
      return parts[parts.length - 2]
    }

    return last
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  const filteredMatches = useMemo(() => {
    if (!normalizedSearchTerm) return []

    const includesTerm = (value) => String(value || '').toLowerCase().includes(normalizedSearchTerm)

    return matches.filter((match) => {
      const searchable = [
        match.title,
        match.sport,
        match.matchType,
        match.location,
        match.address,
        match.city,
        match.description,
        match.organizerName
      ]
      return searchable.some(includesTerm)
    })
  }, [matches, normalizedSearchTerm])

  const filteredUsers = useMemo(() => {
    if (!normalizedSearchTerm) return []

    const includesTerm = (value) => String(value || '').toLowerCase().includes(normalizedSearchTerm)

    return users.filter((userItem) => {
      const searchable = [
        userItem.firstName,
        userItem.lastName,
        `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim(),
        userItem.city,
        userItem.email
      ]
      return searchable.some(includesTerm)
    })
  }, [users, normalizedSearchTerm])

  const filteredCenters = useMemo(() => {
    if (!normalizedSearchTerm) return []

    const includesTerm = (value) => String(value || '').toLowerCase().includes(normalizedSearchTerm)

    return centers.filter((center) => {
      const searchable = [
        center.name,
        center.address,
        center.city,
        ...(center.sports || []),
        ...(center.facilities || [])
      ]
      return searchable.some(includesTerm)
    })
  }, [centers, normalizedSearchTerm])

  const hasSearchResults = filteredMatches.length > 0 || filteredUsers.length > 0 || filteredCenters.length > 0

  const infoCardClass = 'rounded-lg border border-slate-200 bg-white/85 p-2.5 sm:p-2 dark:border-sport-silver/15 dark:bg-sport-darker/60'
  const infoLabelClass = 'flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 dark:text-sport-silver/60'
  const infoValueClass = 'mt-1 text-xs text-slate-800 dark:text-sport-light break-words sm:truncate'
  const joinJoinedButtonClass =
    'cursor-not-allowed rounded-lg border border-gray-500/50 bg-gray-600/30 px-3 py-2.5 text-xs font-semibold text-gray-100 shadow-none sm:text-sm dark:border-gray-600 dark:bg-gray-800/85 dark:text-gray-300'

  return (
    <div className="space-y-4 md:space-y-6">
      {banners.length > 0 && (
        <div>
          <SearchBar
            onSearch={setSearchTerm}
            placeholder={t('search.searchPlaceholder')}
          />
        </div>
      )}

      {normalizedSearchTerm && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-white/90 p-3 shadow-lg dark:bg-sport-darker/80 sm:p-4 md:p-6"
        >
          <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-sport-light sm:text-lg">{t('search.results', { defaultValue: 'Search results' })}</h2>
              <p className="text-xs text-slate-500 dark:text-sport-silver/70 sm:text-sm">{searchTerm}</p>
            </div>
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="rounded-full border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-sport-silver/20 dark:text-sport-silver dark:hover:bg-sport-silver/10 sm:px-3 sm:py-2 sm:text-xs"
            >
              {t('common.clear', { defaultValue: 'Clear' })}
            </button>
          </div>

          {!hasSearchResults ? (
            <div className="rounded-2xl bg-slate-50 p-3 text-xs text-slate-500 dark:bg-sport-dark/70 dark:text-sport-silver/70 sm:p-4 sm:text-sm">
              {t('search.noResultsFound', { term: searchTerm })}
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {filteredMatches.length > 0 && (
                <section className="space-y-2.5 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-sport-silver/70 sm:text-sm">{t('search.matches')}</h3>
                    <span className="text-xs text-slate-400">{filteredMatches.length}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
                    {filteredMatches.slice(0, 3).map((match) => (
                      <Link key={match.id} to={`/match/${match.id}`} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-sport-primary/40 hover:shadow-md dark:border-sport-silver/10 dark:bg-sport-dark/80 sm:p-4">
                        <div className="flex items-start justify-between gap-2.5 sm:gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-sport-light sm:text-base">{match.title || (match.sport === 'calcio' ? t('common.footballMatch') : t('common.padelMatch'))}</p>
                            <p className="text-xs text-slate-500 dark:text-sport-silver/70 sm:text-sm">{getWhere(match)}</p>
                          </div>
                          <span className="rounded-full bg-sport-primary/10 px-2 py-1 text-[10px] font-semibold text-sport-primary sm:px-2.5 sm:py-1 sm:text-[11px]">{getSportDisplay(match)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {filteredUsers.length > 0 && (
                <section className="space-y-2.5 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-sport-silver/70 sm:text-sm">{t('search.players')}</h3>
                    <span className="text-xs text-slate-400">{filteredUsers.length}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
                    {filteredUsers.slice(0, 3).map((player) => (
                      <Link key={player.id} to={`/profile/${player.id}`} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-sport-primary/40 hover:shadow-md dark:border-sport-silver/10 dark:bg-sport-dark/80 sm:p-4">
                        <div className="flex items-center gap-2.5 sm:gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary text-xs font-bold text-white sm:h-11 sm:w-11 sm:text-sm">
                            {player.firstName?.[0]}{player.lastName?.[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-sport-light sm:text-base">{player.firstName} {player.lastName}</p>
                            <p className="text-xs text-slate-500 dark:text-sport-silver/70 sm:text-sm">{player.city || t('common.locationTbd')}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {filteredCenters.length > 0 && (
                <section className="space-y-2.5 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-sport-silver/70 sm:text-sm">{t('search.sportsCenters')}</h3>
                    <span className="text-xs text-slate-400">{filteredCenters.length}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
                    {filteredCenters.slice(0, 3).map((center) => (
                      <Link key={center.id} to="/centers" className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-sport-primary/40 hover:shadow-md dark:border-sport-silver/10 dark:bg-sport-dark/80 sm:p-4">
                        <div className="flex items-start justify-between gap-2.5 sm:gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-sport-light sm:text-base">{center.name}</p>
                            <p className="text-xs text-slate-500 dark:text-sport-silver/70 sm:text-sm">{center.address}</p>
                            <CenterPitchSlots center={center} variant="inline" />
                          </div>
                          <span className="shrink-0 rounded-full bg-sport-primary/10 px-2 py-1 text-[10px] font-semibold text-sport-primary sm:px-2.5 sm:py-1 sm:text-[11px]">
                            {(center.sports || []).slice(0, 2).join(', ')}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Banner Section */}
      {banners.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl gradient-border p-1"
        >
          <div className="space-y-4">
            <div className="relative overflow-hidden rounded-3xl h-[220px] md:h-[300px] bg-sport-dark">
              <AnimatePresence mode="wait">
                <motion.div
                  key={banners[currentBanner]?.id || currentBanner}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: 'easeInOut' }}
                  className="absolute inset-0"
                >
                  <img
                    src={banners[currentBanner]?.imageUrl || ''}
                    alt={banners[currentBanner]?.title || 'Banner'}
                    className="h-full w-full object-cover"
                  />
                </motion.div>
              </AnimatePresence>

            </div>
          </div>
        </motion.div>
      )}

      {/* Upcoming + Welcome Section */}
      <div className="flex flex-col gap-5 md:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl gradient-border p-1 order-2"
        >
          <div className="relative h-full overflow-hidden rounded-3xl bg-sport-dark/90 p-5 sm:p-8 md:p-12">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-sport-primary/10 rounded-full blur-3xl animate-pulse-glow"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-sport-secondary/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-10">
              <div className="text-center md:text-left">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center md:justify-start gap-2 mb-4"
                >
                  <FireIcon className="h-6 w-6 text-sport-secondary animate-pulse" />
                  <span className="text-sport-secondary font-semibold tracking-wider uppercase text-sm">{t('home.hey')} {t('home.welcomeBack')}</span>
                </motion.div>

                {/* Mobile Logo Animation */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.25, duration: 0.5 }}
                  className="relative mb-6 flex justify-center md:hidden"
                >
                  <motion.img
                    src={logo}
                    alt="Giochiamo"
                    className="h-40 w-40 object-contain drop-shadow-2xl"
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <div className="absolute inset-0 bg-sport-primary/20 rounded-full blur-3xl -z-10 animate-pulse-glow"></div>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-4xl md:text-6xl font-bold font-['Orbitron'] mb-4"
                >
                  <span className="text-sport-light">Hey, </span>
                  <span className="text-gradient">{userData?.firstName}!</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl text-sport-silver/80 mb-8 max-w-lg"
                >
                  {t('home.readyToDominate')}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
                >
                  <Link to="/create-match" className="btn-gradient btn-glow inline-flex items-center justify-center space-x-2 px-8 py-4 text-lg">
                    <SparklesIcon className="h-5 w-5" />
                    <span>{t('home.createMatch')}</span>
                  </Link>
                </motion.div>
              </div>

              {/* Desktop Logo Animation: centered in right half */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25, duration: 0.5 }}
                className="relative hidden md:flex items-center justify-center h-full"
              >
                <motion.img
                  src={logo}
                  alt="Giochiamo"
                  className="h-52 w-52 object-contain drop-shadow-2xl"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="absolute inset-0 bg-sport-primary/20 rounded-full blur-3xl -z-10 animate-pulse-glow"></div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Upcoming Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative overflow-hidden rounded-3xl gradient-border p-1 order-1"
        >
          <div className="relative h-full overflow-hidden rounded-3xl bg-sport-dark/90 p-4 sm:p-6 md:p-8">
            <div className="mb-4 flex min-w-0 flex-row items-center justify-between gap-2">
              <h3 className="min-w-0 flex-1 truncate pr-2 text-base sm:text-xl md:text-2xl font-bold text-sport-light font-['Orbitron']">
                {t('matches.upcomingMatches')}
              </h3>
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                <button
                  onClick={handleNearMe}
                  disabled={locationLoading}
                  className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] sm:px-2.5 sm:text-sm font-semibold rounded-lg transition ${
                    nearMeActive
                      ? 'bg-sport-secondary text-white'
                      : 'bg-sport-primary/20 text-sport-primary hover:bg-sport-primary/30'
                  } disabled:opacity-50`}
                >
                  {locationLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <MapPinIcon className="h-4 w-4" />
                  )}
                  <span>{t('matches.nearMe')}</span>
                </button>
                <Link to="/matches" className="whitespace-nowrap text-sport-primary hover:text-sport-secondary transition text-xs sm:text-sm font-semibold">
                  {t('home.viewAll')}
                </Link>
              </div>
            </div>

            {loading ? (
              <div className="card-sport p-8 min-h-[320px] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : matches.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
                {matches.map((match, index) => {
                  const alreadyJoined = Boolean(user && match.participants?.some((p) => p.userId === user.uid))
                  const rosterCap = getMatchRosterCapacity(match)
                  const isFull = rosterCap > 0 && getAcceptedCount(match) >= rosterCap
                  const derivedStatus = getDerivedMatchStatus(match.date, match.status)
                  const joinDisabled =
                    derivedStatus === 'cancelled' || derivedStatus === 'completed' || isFull || alreadyJoined
                  const joinHomeMuted = alreadyJoined || isFull || derivedStatus === 'cancelled' || derivedStatus === 'completed'
                  const joinHomeLabel = isFull && !alreadyJoined
                    ? t('matchDetails.matchFull', { defaultValue: 'Match full' })
                    : t('matchDetails.joinMatch')
                  const statusLabel = derivedStatus === 'cancelled'
                    ? t('matches.cancelled', { defaultValue: 'Cancelled' })
                    : derivedStatus === 'completed'
                      ? t('matches.completed', { defaultValue: 'Completed' })
                      : derivedStatus === 'ongoing'
                        ? t('matches.inProgress', { defaultValue: 'Ongoing' })
                        : t('matches.upcoming', { defaultValue: 'Upcoming' })

                  return (
                    <motion.div
                      key={match.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + index * 0.06 }}
                      className="card-sport p-3 md:p-4 group hover:border-sport-primary/50 transition-all duration-300 h-full"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xl">{getSportIcon(match.sport)}</span>
                          <span className="badge badge-success text-xs">{statusLabel}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 mb-3">
                        <h3 className="min-w-0 flex-1 text-base font-bold text-sport-light font-['Orbitron'] line-clamp-2 pr-2">
                          {match.title || (match.sport === 'calcio' ? t('common.footballMatch') : t('common.padelMatch'))}
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <UserIcon className="h-3 w-3 text-sport-primary" />
                            <span>{t('matches.organizer')}</span>
                          </div>
                          <p className={infoValueClass}>{match.organizerName || '-'}</p>
                        </div>

                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <TrophyIcon className="h-3 w-3 text-sport-secondary" />
                            <span>{t('sports.sport')}</span>
                          </div>
                          <p className={infoValueClass}>{getSportDisplay(match)}</p>
                        </div>

                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <MapPinIcon className="h-3 w-3 text-sport-accent" />
                            <span>{t('auth.city')}</span>
                          </div>
                          <p className={infoValueClass}>{getCity(match)}</p>
                        </div>

                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <CalendarIcon className="h-3 w-3 text-sport-primary" />
                            <span>{t('matches.date', { defaultValue: 'Date' })}</span>
                          </div>
                          <p className={infoValueClass}>{getMatchDate(match)}</p>
                        </div>

                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <ClockIcon className="h-3 w-3 text-sport-secondary" />
                            <span>{t('matches.time')}</span>
                          </div>
                          <p className={infoValueClass}>{getMatchTime(match)}</p>
                        </div>

                        <div className={infoCardClass}>
                          <div className={infoLabelClass}>
                            <MapPinIcon className="h-3 w-3 text-sport-primary" />
                            <span>{t('matches.location')}</span>
                          </div>
                          <p className={infoValueClass}>{getWhere(match)}</p>
                        </div>

                        {nearMeActive && (
                          <div className={infoCardClass}>
                            <div className={infoLabelClass}>
                              <MapPinIcon className="h-3 w-3 text-sport-accent" />
                              <span>{t('matches.distance')}</span>
                            </div>
                            <p className={infoValueClass}>
                              {getMatchDistance(match) !== null
                                ? `${getMatchDistance(match).toFixed(1)} km`
                                : '-'}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex flex-col gap-2 min-[380px]:flex-row min-[380px]:items-center">
                        <button
                          type="button"
                          onClick={() => handleJoinMatch(match)}
                          disabled={joinDisabled}
                          title={isFull && !alreadyJoined ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
                          className={
                            joinHomeMuted
                              ? `${joinJoinedButtonClass} inline-flex flex-1 items-center justify-center whitespace-nowrap disabled:opacity-100`
                              : 'btn-gradient inline-flex flex-1 items-center justify-center px-3 py-2.5 text-xs sm:text-sm whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50'
                          }
                        >
                          {joinHomeLabel}
                        </button>
                        <Link
                          to={`/match/${match.id}`}
                          className="btn-outline inline-flex items-center justify-center px-3 py-2.5 text-xs sm:text-sm whitespace-nowrap flex-1"
                        >
                          {t('matches.viewDetails')}
                        </Link>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="card-sport p-8 min-h-[320px] flex flex-col items-center justify-center text-center">
                <TrophyIcon className="h-14 w-14 text-sport-silver/35 mb-4" />
                <p className="text-sport-silver/70 mb-4">{t('home.youHaveNoUpcomingMatches')}</p>
                <Link to="/matches" className="btn-outline inline-flex items-center justify-center">
                  {t('home.browseMatches')}
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
      >
        {[
          { label: t('home.totalMatches'), value: stats.totalMatches, icon: CalendarIcon, color: 'text-sport-primary' },
          { label: t('home.activePlayers'), value: stats.activePlayers, icon: UserGroupIcon, color: 'text-sport-secondary' },
          { label: t('home.sportsCenters'), value: stats.sportsCenters, icon: TrophyIcon, color: 'text-sport-accent' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="card-sport p-6 group hover:border-sport-primary/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sport-silver/60 text-sm uppercase tracking-wider mb-1">{stat.label}</p>
                <p className="text-4xl font-bold text-sport-light font-['Orbitron']">{stat.value}</p>
              </div>
              <div className={`p-4 rounded-xl bg-sport-darker ${stat.color} group-hover:scale-110 transition-transform duration-300`}>
                <stat.icon className="h-8 w-8" />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="text-3xl font-bold text-sport-light mb-5 md:mb-8 font-['Orbitron']">
          <span className="text-gradient">{t('home.whyChooseUs')}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="card-sport p-6 text-center group hover:border-sport-primary/50 transition-all duration-300"
            >
              <div className="mb-4 flex justify-center">
                <div className="p-4 rounded-2xl bg-sport-darker text-sport-primary group-hover:scale-110 group-hover:text-sport-secondary transition-all duration-300">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-lg font-bold text-sport-light mb-2 font-['Orbitron']">
                {feature.title}
              </h3>
              <p className="text-sport-silver/70 text-sm">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <div className="hidden justify-center pt-2 md:flex">
        <a
          href="mailto:giochiamo.app@gmail.com"
          className="text-center text-xs text-sport-silver/55 transition hover:text-sport-primary break-all sm:text-sm"
        >
          giochiamo.app@gmail.com
        </a>
      </div>

    </div>
  )
}

export default Home
